import { PageController } from '@client/components/_core/layout/PageController';
import { PageLayoutHeader } from '@client/components/_core/layout/PageLayout/PageLayoutHeader';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { message } from '@client/components/_ui/feedback/message';
import { ListPagination } from '@client/components/_ui/navigation';
import { Link } from '@client/components/Common/Link';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import type { RouterOutputs } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { AUTHORISATION_NOTE_MAX_LENGTH } from '@shared/constants/business/fleet-portal';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Input, Modal } from 'antd';
import { BadgeCheck, Check, FileText, MessageCircleQuestion, X } from 'lucide-react';
import { useState } from 'react';
import { match } from 'ts-pattern';
import { z } from 'zod';
import { PortalSectionHeading } from './PortalMetrics';
import { claimRef, daysSince, isAuthorityDeniedError } from './portal-utils';
import { PortalScopeSelect, usePortalScope } from './usePortalScope';

const authorisationsSearchSchema = z.object({
    fleetClientId: z
        .string()
        .regex(/^[1-9]\d*$/)
        .optional()
        .catch(undefined),
});

const DECIDED_STATUSES = ['APPROVED', 'DECLINED', 'SUPERSEDED'] as const;

export const Route = createFileRoute('/console/$orgId/fleet-portal/authorisations')({
    staticData: {
        ui: {
            title: 'Authorisations',
            subtitle: 'Repair authorisations requiring your approval',
            icon: BadgeCheck,
        },
    },
    validateSearch: zodValidator(authorisationsSearchSchema),
    component: FleetPortalAuthorisationsPage,
});

type PortalAuthorisation = RouterOutputs['fleetPortal']['listAuthorisations']['rows'][number];
type Decision = 'decline' | 'query';

function FleetPortalAuthorisationsPage() {
    return (
        <PageController route={Route}>
            <AuthorisationsContent />
        </PageController>
    );
}

function AuthorisationsContent() {
    const { trpc, trpcUtils } = useTrpc();
    const { fleetClientId: fleetClientIdSearch } = Route.useSearch();
    const navigate = Route.useNavigate();
    const scope = usePortalScope(fleetClientIdSearch ? BigInt(fleetClientIdSearch) : undefined, () => {
        // Selected client left the caller's scope — drop it from the URL too so
        // the deep link stops naming a client the queries no longer honour.
        void navigate({ search: {}, replace: true });
    });
    const [pendingOffset, setPendingOffset] = useState(0);
    const [historyOffset, setHistoryOffset] = useState(0);
    const [actingId, setActingId] = useState<bigint | null>(null);
    const [decision, setDecision] = useState<{ kind: Decision; row: PortalAuthorisation } | null>(null);
    const queryScope = { fleetClientId: scope.fleetClientId };
    const queriesEnabled = !scope.isLoading && !scope.error;
    const pendingAuthorisationsQuery = trpc.fleetPortal.listAuthorisations.useQuery(
        {
            ...queryScope,
            status: 'PENDING',
            limit: DEFAULT_PAGE_SIZE,
            offset: pendingOffset,
        },
        { enabled: queriesEnabled },
    );
    const authorisationHistoryQuery = trpc.fleetPortal.listAuthorisations.useQuery(
        {
            ...queryScope,
            statuses: [...DECIDED_STATUSES],
            limit: DEFAULT_PAGE_SIZE,
            offset: historyOffset,
        },
        { enabled: queriesEnabled },
    );

    const setFleetClientId = (fleetClientId: bigint | undefined) => {
        scope.setFleetClientId(fleetClientId);
        setPendingOffset(0);
        setHistoryOffset(0);
        void navigate({
            search: fleetClientId === undefined ? {} : { fleetClientId: fleetClientId.toString() },
            replace: true,
        });
    };

    const onDecided = (text: string, claimId: bigint) => {
        message.success(text);
        setPendingOffset(0);
        setHistoryOffset(0);
        setDecision(null);
        void trpcUtils.fleetPortal.listAuthorisations.invalidate();
        void trpcUtils.fleetPortal.getDashboardStats.invalidate();
        void trpcUtils.fleetPortal.listClaims.invalidate();
        void trpcUtils.fleetPortal.getClaimDetail.invalidate({ claimId });
        // The AuthorityBar reads the caller's cap from the portal context; a decision
        // may follow a cap change by an admin, so refresh it with the lists.
        void trpcUtils.fleetPortal.getPortalContext.invalidate();
    };
    const mutationOptions = (text: string) => ({
        onSuccess(row: Pick<PortalAuthorisation, 'claimId'>) {
            onDecided(text, row.claimId);
        },
        onError(error: unknown) {
            errorMessage.show(error);
            if (isAuthorityDeniedError(error)) {
                // The cap the page rendered against no longer covers the amount:
                // refetch the context (AuthorityBar) and the rows' above-authority flag.
                void trpcUtils.fleetPortal.getPortalContext.invalidate();
                void trpcUtils.fleetPortal.listAuthorisations.invalidate();
            }
        },
        onSettled() {
            setActingId(null);
        },
    });
    const approveMutation = trpc.fleetPortal.approveAuthorisation.useMutation(
        mutationOptions('Authorisation approved'),
    );
    const declineMutation = trpc.fleetPortal.declineAuthorisation.useMutation(
        mutationOptions('Authorisation declined'),
    );
    const queryMutation = trpc.fleetPortal.queryAuthorisation.useMutation(
        mutationOptions('Query sent to the claims handler'),
    );
    const isAnyActing = approveMutation.isPending || declineMutation.isPending || queryMutation.isPending;

    if (scope.error) {
        return (
            <div>
                <PageLayoutHeader />
                <InlineQueryError error={scope.error} onRetry={scope.refetch} />
            </div>
        );
    }

    if (pendingAuthorisationsQuery.error || authorisationHistoryQuery.error) {
        return (
            <InlineQueryError
                error={pendingAuthorisationsQuery.error ?? authorisationHistoryQuery.error}
                onRetry={() => {
                    void pendingAuthorisationsQuery.refetch();
                    void authorisationHistoryQuery.refetch();
                }}
            />
        );
    }

    if (pendingAuthorisationsQuery.isPending || authorisationHistoryQuery.isPending) {
        return <Loading className="min-h-64" />;
    }

    const pending = pendingAuthorisationsQuery.data.rows;
    const decided = authorisationHistoryQuery.data.rows;
    const pendingTotal = pendingAuthorisationsQuery.data.total;
    const historyTotal = authorisationHistoryQuery.data.total;

    return (
        <div className="max-w-[1120px]">
            <PageLayoutHeader
                rightAddon={
                    scope.clients.length > 1 ? <PortalScopeSelect scope={{ ...scope, setFleetClientId }} /> : undefined
                }
            />
            <p className="mb-4 text-muted-foreground text-sm">
                {pendingTotal === 1
                    ? '1 repair authorisation awaiting your approval'
                    : `${pendingTotal.toLocaleString()} repair authorisations awaiting your approval`}
            </p>

            {pendingTotal === 0 ? (
                <div className="rounded-xl border border-separator bg-card p-5 text-muted-foreground text-sm">
                    No repair authorisations awaiting your approval.
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                    {pending.map((row) => (
                        <PendingAuthorisationCard
                            key={row.id.toString()}
                            row={row}
                            callerAuthorityLimit={scope.authorityLimitFor(row.fleetClientId)}
                            isActing={isAnyActing && actingId === row.id}
                            isAnyActing={isAnyActing}
                            onApprove={() => {
                                setActingId(row.id);
                                approveMutation.mutate({ id: row.id });
                            }}
                            onDecline={() => setDecision({ kind: 'decline', row })}
                            onQuery={() => setDecision({ kind: 'query', row })}
                        />
                    ))}
                </div>
            )}

            {pendingTotal > DEFAULT_PAGE_SIZE && (
                <ListPagination
                    current={Math.floor(pendingOffset / DEFAULT_PAGE_SIZE) + 1}
                    total={pendingTotal}
                    pageSize={DEFAULT_PAGE_SIZE}
                    onChange={(page) => setPendingOffset((page - 1) * DEFAULT_PAGE_SIZE)}
                    variant="compact"
                    showTotal
                />
            )}

            {historyTotal > 0 && (
                <div className="mt-8">
                    <PortalSectionHeading label="Decided" count={historyTotal} className="mb-3" />
                    <div className="overflow-hidden rounded-xl border border-separator bg-card">
                        {decided.map((row) => (
                            <DecidedAuthorisationRow key={row.id.toString()} row={row} />
                        ))}
                    </div>
                    {historyTotal > DEFAULT_PAGE_SIZE && (
                        <ListPagination
                            current={Math.floor(historyOffset / DEFAULT_PAGE_SIZE) + 1}
                            total={historyTotal}
                            pageSize={DEFAULT_PAGE_SIZE}
                            onChange={(page) => setHistoryOffset((page - 1) * DEFAULT_PAGE_SIZE)}
                            variant="compact"
                            showTotal
                        />
                    )}
                </div>
            )}

            {decision && (
                <DecisionNoteModal
                    kind={decision.kind}
                    row={decision.row}
                    isSubmitting={isAnyActing}
                    onCancel={() => setDecision(null)}
                    onSubmit={(note) => {
                        setActingId(decision.row.id);
                        if (decision.kind === 'decline') {
                            declineMutation.mutate({ id: decision.row.id, note });
                        } else {
                            queryMutation.mutate({ id: decision.row.id, note });
                        }
                    }}
                />
            )}
        </div>
    );
}

/**
 * The mandatory note behind Decline and Query (CUR-1052). Who and when are
 * recorded server-side; this collects the why.
 */
function DecisionNoteModal({
    kind,
    row,
    isSubmitting,
    onCancel,
    onSubmit,
}: {
    kind: Decision;
    row: PortalAuthorisation;
    isSubmitting: boolean;
    onCancel: () => void;
    onSubmit: (note: string) => void;
}) {
    const { formatCurrency } = useFormatter();
    const [note, setNote] = useState('');
    const trimmed = note.trim();
    const copy =
        kind === 'decline'
            ? {
                  title: 'Decline authorisation',
                  label: 'Reason for declining',
                  help: 'The reason is recorded on the claim and passed to the repairer by the claims handler.',
                  ok: 'Decline',
              }
            : {
                  title: 'Query authorisation',
                  label: 'Your question for the repairer',
                  help: "The claims handler relays your question. The request stays pending; the repairer's revised quote arrives as a new request.",
                  ok: 'Send query',
              };

    return (
        <Modal
            open
            title={copy.title}
            okText={copy.ok}
            okButtonProps={{ danger: kind === 'decline', disabled: trimmed.length === 0, loading: isSubmitting }}
            cancelButtonProps={{ disabled: isSubmitting }}
            onOk={() => onSubmit(trimmed)}
            onCancel={onCancel}
            destroyOnHidden
        >
            <p className="mb-3 text-muted-foreground text-sm">
                {row.repairerName ?? 'Repairer to be confirmed'} · {formatCurrency(Number(row.amount))}{' '}
                {row.gstInclusive ? 'inc. GST' : 'ex GST'}
                {row.registration && ` · ${row.registration}`}
            </p>
            <label className="mb-1.5 block font-medium text-sm" htmlFor="authorisation-decision-note">
                {copy.label}
            </label>
            <Input.TextArea
                id="authorisation-decision-note"
                className="mb-6"
                autoFocus
                rows={4}
                maxLength={AUTHORISATION_NOTE_MAX_LENGTH}
                showCount
                value={note}
                onChange={(event) => setNote(event.target.value)}
                disabled={isSubmitting}
            />
            <p className="mt-2 text-subtle-foreground text-xs">{copy.help}</p>
        </Modal>
    );
}

function AmountBlock({ row }: { row: PortalAuthorisation }) {
    const { formatCurrency } = useFormatter();

    return (
        <div className="text-right">
            <div className="font-bold text-2xl text-foreground">{formatCurrency(Number(row.amount))}</div>
            <div className="text-subtle-foreground text-xs">
                {row.gstInclusive ? 'Total inc. GST' : 'ex GST'} ·{' '}
                {row.amountBasis === 'ESTIMATE' ? 'estimate' : 'assessed'}
            </div>
        </div>
    );
}

function AuthorisationSummary({ row }: { row: PortalAuthorisation }) {
    const { formatDate } = useFormatter();
    const orgId = useOrgId();
    const vehicleLine = [row.registration, row.vehicle].filter(Boolean).join(' · ');

    return (
        <div>
            <div className="font-semibold text-foreground">{row.repairerName ?? 'Repairer to be confirmed'}</div>
            {vehicleLine && <div className="text-muted-foreground text-sm">{vehicleLine}</div>}
            <div className="mt-1 text-subtle-foreground text-xs">
                <Link
                    to="/console/$orgId/fleet-portal/claims/details/$claimId"
                    params={{ orgId, claimId: row.claimId }}
                    className="font-semibold text-sm"
                >
                    Claim {claimRef({ id: row.claimId, generatedId: row.generatedId, externalId: row.externalId })}
                </Link>{' '}
                · submitted {formatDate(row.createdAt)}
            </div>
        </div>
    );
}

/**
 * Quote detail before deciding (CUR-1052): the register's component totals and
 * the assessed estimate report from Clear21, when it has been fetched. The
 * fleet manager never enters Clear21.
 */
function QuoteDetail({ row }: { row: PortalAuthorisation }) {
    const { formatCurrency } = useFormatter();
    const breakdown = row.quoteBreakdown;
    const parts: Array<[label: string, amount: number]> = [];
    if (breakdown) {
        for (const [label, amount] of [
            ['Parts', breakdown.partsExTax],
            ['Labour', breakdown.labourExTax],
            ['Sublet', breakdown.subletExTax],
            ['Other', breakdown.otherExTax],
        ] as const) {
            if (amount !== null && amount > 0) {
                parts.push([label, amount]);
            }
        }
    }

    if (parts.length === 0 && !row.quoteReportUrl) {
        return null;
    }

    return (
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs">
            {parts.map(([label, amount]) => (
                <span key={label} className="text-muted-foreground">
                    {label}{' '}
                    <span className="font-semibold text-foreground tabular-nums">
                        {formatCurrency(amount, { maximumFractionDigits: 0 })}
                    </span>
                </span>
            ))}
            {row.quoteReportUrl && (
                <a
                    href={row.quoteReportUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 font-semibold text-accent-600"
                >
                    <FileText className="size-3.5" />
                    View assessed estimate
                </a>
            )}
        </div>
    );
}

/** "Pending · N days" pill showing how long an authorisation has been waiting. */
function PendingAgeBadge({ createdAt }: { createdAt: Date | null }) {
    const days = daysSince(createdAt);

    return (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-warning-border bg-warning/10 px-2.5 py-0.5 font-semibold text-warning-foreground text-xs">
            <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-current" />
            {days === null ? 'Pending' : `Pending · ${days} ${days === 1 ? 'day' : 'days'}`}
        </span>
    );
}

/**
 * Requested amount against the caller's OWN authority (CUR-1052): rendered only
 * for a capped fleet manager. Uncapped users can approve any amount, so there
 * is no limit to draw. A bar with a marker at the cap, and the amount above it.
 */
function AuthorityBar({
    row,
    callerAuthorityLimit,
}: {
    row: PortalAuthorisation;
    callerAuthorityLimit: number | null;
}) {
    const { formatCurrency } = useFormatter();
    const amount = Number(row.amount);

    if (callerAuthorityLimit === null || amount <= callerAuthorityLimit) {
        return null;
    }

    return (
        <div className="mt-3.5 max-w-[560px]">
            <div className="relative h-2 rounded-full bg-warning">
                <span
                    aria-hidden
                    className="absolute -top-[3px] -bottom-[3px] w-0.5 rounded-full bg-foreground opacity-50"
                    style={{ left: `${Math.round((callerAuthorityLimit / amount) * 100)}%` }}
                />
            </div>
            <div className="mt-1.5 flex items-baseline justify-between gap-4 text-subtle-foreground text-xs">
                <span>Your authority · {formatCurrency(callerAuthorityLimit, { maximumFractionDigits: 0 })}</span>
                <span className="font-semibold text-warning-foreground">
                    {formatCurrency(amount - callerAuthorityLimit, { maximumFractionDigits: 0 })} above your limit
                </span>
            </div>
        </div>
    );
}

function PendingAuthorisationCard({
    row,
    callerAuthorityLimit,
    isActing,
    isAnyActing,
    onApprove,
    onDecline,
    onQuery,
}: {
    row: PortalAuthorisation;
    callerAuthorityLimit: number | null;
    isActing: boolean;
    isAnyActing: boolean;
    onApprove: () => void;
    onDecline: () => void;
    onQuery: () => void;
}) {
    const { formatDate } = useFormatter();
    // An answered query (the handler completed the relay task without a revised
    // quote) no longer blocks a further question.
    const isQueried = row.queriedAt !== null && !row.queryAnswered;
    const approvedInClear21 = row.sourceApprovalStatus?.toLowerCase() === 'authorised';

    return (
        <div className="rounded-xl border border-separator bg-card p-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <AuthorisationSummary row={row} />
                <div className="flex flex-wrap items-start justify-end gap-4">
                    {isQueried ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-info-border bg-info/10 px-2.5 py-0.5 font-semibold text-info-foreground text-xs">
                            <MessageCircleQuestion className="size-3.5" />
                            Queried
                        </span>
                    ) : (
                        <PendingAgeBadge createdAt={row.createdAt} />
                    )}
                    <AmountBlock row={row} />
                </div>
            </div>
            {row.description && <p className="mt-3 text-foreground text-sm">{row.description}</p>}
            <QuoteDetail row={row} />
            {approvedInClear21 && (
                <div className="mt-3 rounded-lg border border-warning-border bg-warning/10 px-3 py-2 text-warning-foreground text-xs">
                    <span className="font-semibold">Already approved in Clear21</span>
                    {row.sourceAuthorityDate && ` on ${formatDate(row.sourceAuthorityDate)}`}. The assessor authorised
                    these repairs directly; record your decision here so the claim reflects it.
                </div>
            )}
            {row.queriedAt !== null && (
                <div className="mt-3 rounded-lg border border-info-border bg-info/10 px-3 py-2 text-xs">
                    <div className="font-semibold text-info-foreground">
                        {row.queryAnswered ? 'Query answered' : 'Queried'}
                        {row.queriedByName && ` · asked by ${row.queriedByName}`}
                        {row.queriedAt && ` · ${formatDate(row.queriedAt)}`}
                    </div>
                    <div className="mt-0.5 text-foreground">{row.queryNote}</div>
                    <div className="mt-1 text-muted-foreground">
                        {row.queryAnswered
                            ? 'The claims handler has answered without a revised quote. Approve or decline this quote, or ask a further question.'
                            : 'The claims handler is relaying it to the repairer. A revised quote arrives as a new request; you can still approve or decline this one.'}
                    </div>
                </div>
            )}
            <AuthorityBar row={row} callerAuthorityLimit={callerAuthorityLimit} />
            <div className="mt-4 flex flex-wrap items-center gap-2">
                {row.isAboveCallerAuthority ? (
                    <span className="text-muted-foreground text-sm">
                        Above your authority ·{' '}
                        {row.approverName ? (
                            <>
                                sent to <span className="font-semibold text-foreground">{row.approverName}</span>
                            </>
                        ) : (
                            'needs an approver with sufficient authority'
                        )}
                    </span>
                ) : (
                    <>
                        <Button
                            type="primary"
                            icon={<Check />}
                            loading={isActing}
                            disabled={isAnyActing}
                            onClick={onApprove}
                        >
                            Approve
                        </Button>
                        <Button
                            icon={<MessageCircleQuestion />}
                            disabled={isAnyActing || isQueried}
                            onClick={onQuery}
                            title={isQueried ? 'Awaiting the answer to your query' : undefined}
                        >
                            Query
                        </Button>
                        <Button danger icon={<X />} disabled={isAnyActing} onClick={onDecline}>
                            Decline
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
}

function DecidedAuthorisationRow({ row }: { row: PortalAuthorisation }) {
    const { formatDate, formatCurrency } = useFormatter();
    const orgId = useOrgId();
    const vehicleLine = [row.registration, row.vehicle].filter(Boolean).join(' · ');

    const statusLine = match(row.status)
        .with('APPROVED', () => (
            <span className="text-success-foreground">
                {row.isAutoApproved
                    ? 'Auto-approved · within authority'
                    : `Approved${row.decidedByName ? ` by ${row.decidedByName}` : ''}`}
                {row.decidedAt && ` · ${formatDate(row.decidedAt)}`}
                {row.clear21ApprovalSentAt && ' · authority sent to repairer'}
            </span>
        ))
        .with('DECLINED', () => (
            <span className="text-danger-foreground">
                Declined{row.decidedByName ? ` by ${row.decidedByName}` : ''}
                {row.decidedAt && ` · ${formatDate(row.decidedAt)}`}
            </span>
        ))
        .with('SUPERSEDED', () => (
            <span className="text-subtle-foreground">
                {row.queriedAt ? 'Query answered · revised quote received' : 'Superseded by a newer revision'}
            </span>
        ))
        .otherwise(() => null);

    return (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 border-separator border-t bg-card-header-bg px-5 py-3 first:border-t-0">
            <div className="min-w-0 flex-1">
                <div className="font-semibold text-foreground text-sm">
                    {row.repairerName ?? 'Repairer to be confirmed'}
                </div>
                <div className="text-subtle-foreground text-xs">
                    {vehicleLine && `${vehicleLine} · `}
                    <Link
                        to="/console/$orgId/fleet-portal/claims/details/$claimId"
                        params={{ orgId, claimId: row.claimId }}
                        className="font-semibold"
                    >
                        {claimRef({ id: row.claimId, generatedId: row.generatedId, externalId: row.externalId })}
                    </Link>
                </div>
                {row.status === 'DECLINED' && row.declineReason && (
                    <div className="mt-1 text-foreground text-xs">“{row.declineReason}”</div>
                )}
            </div>
            <div className="whitespace-nowrap text-xs">{statusLine}</div>
            <div
                className="whitespace-nowrap font-bold text-foreground text-sm tabular-nums"
                title={`${row.gstInclusive ? 'Total inc. GST' : 'ex GST'} · ${row.amountBasis === 'ESTIMATE' ? 'estimate' : 'assessed'}`}
            >
                {formatCurrency(Number(row.amount))}
            </div>
        </div>
    );
}
